package com.klantenstop.web.pages.authentication.verifyEmail

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.data.models.domain.VerificationStatus
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.klantenstop.web.utils.Constants
import com.materialkobweb.utils.rememberViewModel
import com.materialkobweb.components.widgets.FilledButton
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.materialkobweb.styles.MaterialColorVars
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@Page("/verifieren")
@Composable
fun VerifyEmailPage() {
    val pageContext = rememberPageContext()

    // Extract code from URL query parameters
    val code = remember { pageContext.route.params["code"] ?: "" }

    val viewModel = rememberViewModel(cached = false) { VerifyEmailViewModel(code) }
    val state by viewModel.state.collectAsState()

    // Handle effects
    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is VerifyEmailContract.Effect.NavigateToHome -> {
                    pageContext.router.navigateTo(Navigation.Screen.Home.route, UpdateHistoryMode.REPLACE)
                }
            }
        }
    }

    VerifyEmailPageContent(
        state = state,
        sendIntent = viewModel::sendIntent
    )
}

@Composable
private fun VerifyEmailPageContent(
    state: VerifyEmailContract.State,
    sendIntent: (VerifyEmailContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        modifier = Modifier.fillMaxSize(),
        title = "E-mail verifiëren"
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.SM) 80.percent else 90.percent)
                .fillMaxHeight(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(Constants.SECTION_WIDTH.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                val titleColor = when (state.verificationStatus) {
                    is VerificationStatus.Success,
                    is VerificationStatus.AlreadyVerified,
                    is VerificationStatus.SuccessfullySent -> MaterialColorVars.Primary.value()

                    is VerificationStatus.Expired,
                    is VerificationStatus.Error,
                    is VerificationStatus.FailedToSend -> MaterialColorVars.Error.value()

                    else -> MaterialColorVars.OnSurface.value()
                }

                H1Heading(
                    modifier = Modifier
                        .textAlign(TextAlign.Center),
                    typography = Typography.HeadlineSmall,
                    text = state.verificationStatus.title,
                    color = titleColor
                )

                SpanText(
                    modifier = Modifier
                        .textAlign(TextAlign.Center)
                        .fontSize(16.px)
                        .lineHeight(24.px)
                        .margin(top = 16.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value())
                        .whiteSpace(WhiteSpace.PreWrap),
                    text = state.verificationStatus.description
                )

                if (state.verificationStatus.buttonText != null) {
                    FilledButton(
                        modifier = Modifier.margin(top = 24.px),
                        onClick = {
                            when (state.verificationStatus) {
                                is VerificationStatus.Success,
                                is VerificationStatus.AlreadyVerified -> sendIntent(VerifyEmailContract.Intent.NavigateToHome)

                                is VerificationStatus.Expired -> sendIntent(VerifyEmailContract.Intent.ResendVerification)
                                else -> Unit
                            }
                        }
                    ) {
                        SpanText(state.verificationStatus.buttonText!!)
                    }
                }
            }
        }
    }
}
